/** Typed client of the dashboard API (`/api/v1`), generated types from packages/shared-types. */
import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "@solar/shared-types";
import { authHeaders } from "../auth/auth";
import { config } from "../config";
import { translateOutside, translatedProblem } from "../i18n/problems";

export type S = components["schemas"];

export class ApiError extends Error {
  status: number;
  problem: Partial<S["Problem"]>;
  constructor(status: number, problem: ApiError["problem"]) {
    // the reader's language for a problem code the catalog knows (i18n/problems.ts), else the server's sentence
    super(translatedProblem(problem) || problem.detail || problem.title || translateOutside("errors.generic") || `Request failed (${status})`);
    this.status = status;
    this.problem = problem;
  }
}

let activeOrganization: string | null = null;

/** The company the dashboard shows: sent as `X-Organization-Id`, and the API refuses anything of another company
 * in that request. */
export function setActiveOrganization(id: string | null) {
  activeOrganization = id;
}

function companyHeaders(): Record<string, string> {
  return activeOrganization ? { "X-Organization-Id": activeOrganization } : {};
}

/** The last authentication headers sent: a request made while the page unloads cannot wait for them. */
let lastAuth: Record<string, string> = {};

const auth: Middleware = {
  async onRequest({ request }) {
    lastAuth = await authHeaders();
    const headers = { ...lastAuth, ...companyHeaders() };
    for (const [k, v] of Object.entries(headers)) request.headers.set(k, v);
    return request;
  },
};

/** Sends a JSON request that outlives the page (fetch keepalive: tab close, reload, a full navigation), with the
 * last authentication headers; synchronous, so it can run in `pagehide` / `beforeunload`. False when it could not
 * be sent (no fetch, or the body is over the browser's 64 KB keepalive limit). */
export function sendKeepalive(method: "PUT" | "POST", path: string, body: unknown): boolean {
  try {
    const json = JSON.stringify(body);
    if (json.length > 60_000 || typeof fetch !== "function") return false;
    void fetch(`${config.apiUrl}/api${path}`, {
      method,
      keepalive: true,
      headers: { "Content-Type": "application/json", ...lastAuth, ...companyHeaders() },
      body: json,
    }).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

export const api = createClient<paths>({ baseUrl: `${config.apiUrl}/api` });
api.use(auth);

/** Unwraps an openapi-fetch result: the data, or an ApiError. */
export async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await promise;
  if (!response.ok) {
    // not our problem JSON (a gateway's or proxy's error page): never its words ("Bad Gateway", a server's name),
    // our generic sentence instead (CLAUDE.md, user-facing messages)
    const problem = (error && typeof error === "object" ? error : {}) as ApiError["problem"];
    throw new ApiError(response.status, problem);
  }
  return data as T;
}

/** A raw authenticated request (images, multipart uploads). */
export async function rawFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries({ ...(await authHeaders()), ...companyHeaders() })) headers.set(k, v);
  const response = await fetch(`${config.apiUrl}${path}`, { ...init, headers });
  if (!response.ok) {
    let problem: ApiError["problem"] = {}; // not JSON: our generic sentence, never the gateway's words
    try {
      problem = await response.json();
    } catch {
      /* not JSON */
    }
    throw new ApiError(response.status, problem);
  }
  return response;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = error.problem.errors?.map((e) => `${(e.loc || []).join(".")}: ${e.msg}`);
    return fields?.length ? `${error.message} — ${fields.join("; ")}` : error.message;
  }
  // fetch's own failure (offline, DNS, CORS): the browser's wording is English and technical
  if (error instanceof TypeError && /fetch|network|load failed/i.test(error.message)) return translateOutside("errors.offline") ?? error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}
